"use client";

import type { ReactNode } from "react";
import { AnalyticsCard } from "@/components/product/AnalyticsCard";
import { Button } from "@/components/ui/Button";
import { Check, Clock } from "@/components/ui/icons";
import { LINKS, type TourApp } from "@/content/quikit";
import { cn } from "@/lib/utils";

const white = "rounded-[18px] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]";

/* ------------------------------------------------------------------ */
/*  Shared panel body: copy + checklist + CTAs + app mock              */
/* ------------------------------------------------------------------ */
export function TourBody({ app, mock }: { app: TourApp; mock: ReactNode }) {
  return (
    <>
      <p data-story-item="" className="max-w-[58ch] text-[14px] leading-[1.65] text-muted">
        {app.sub}
      </p>
      <ul className="mt-6 grid gap-3 sm:grid-cols-3">
        {app.items.map((item) => (
          <li key={item.name} data-story-item="" className={cn(white, "p-4")}>
            <span className="grid size-6 place-items-center rounded-full bg-accent-soft text-accent">
              <Check className="size-3.5" strokeWidth={2.2} />
            </span>
            <p className="mt-3 text-[14px] font-medium">{item.name}</p>
            <p className="mt-1 text-[12px] leading-[1.55] text-muted">{item.desc}</p>
          </li>
        ))}
      </ul>
      <div data-story-item="" className="mt-6">
        {mock}
      </div>
      <div data-story-item="" className="mt-6 flex flex-wrap gap-2">
        <Button size="sm" href={LINKS.register}>
          {`Start ${app.name} free`}
        </Button>
        <Button size="sm" variant="outline" href={LINKS.demo}>
          Book a demo
        </Button>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  App mocks                                                          */
/* ------------------------------------------------------------------ */
export function CrmMock() {
  const cols: [string, [string, string, string][]][] = [
    ["Qualified", [["Acme Retail", "$4,200", "AS"], ["Northwind", "$2,800", "RK"]]],
    ["Proposal", [["Globex Ltd", "$6,500", "JS"], ["Umbrella Co", "$1,900", "AG"]]],
    ["Negotiation", [["Initech", "$3,100", "PM"]]],
    ["Won", [["Stark Infra", "$9,400", "AS"]]],
  ];
  return (
    <div className={cn(white, "p-4")}>
      <div className="flex items-center justify-between">
        <p className="text-[14px] font-medium">Sales pipeline</p>
        <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent">$42,600 open</span>
      </div>
      <div className="no-scrollbar mt-4 grid grid-cols-4 gap-2 overflow-x-auto">
        {cols.map(([stage, deals]) => (
          <div key={stage} className="min-w-[120px] rounded-[14px] bg-panel p-2">
            <p className="flex justify-between px-1 text-[10.5px] font-medium text-muted">
              {stage} <span>{deals.length}</span>
            </p>
            <div className="mt-2 space-y-2">
              {deals.map(([name, value, owner]) => (
                <div key={name} className="rounded-[10px] bg-white p-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <p className="text-[11.5px] font-medium">{name}</p>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="text-[11px] text-muted">{value}</span>
                    <span className="grid size-5 place-items-center rounded-full bg-lilac text-[8px] font-semibold text-lilac-ink">{owner}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProjectsMock() {
  const rows: [string, string, string, number][] = [
    ["QP-214", "Checkout redesign", "In review", 90],
    ["QP-221", "SSO for enterprise tenants", "In progress", 55],
    ["QP-230", "Jira import — attachments", "In progress", 38],
    ["QP-236", "Release 4.2 test plan", "To do", 8],
  ];
  const tone: Record<string, string> = {
    "In review": "bg-[#e3ecfc] text-[#3d6fd8]",
    "In progress": "bg-accent-soft text-accent",
    "To do": "bg-panel text-muted",
  };
  return (
    <div className={cn(white, "p-4")}>
      <div className="flex items-center justify-between">
        <p className="text-[14px] font-medium">Sprint 18 · Web platform</p>
        <span className="text-[11px] text-muted">6 days left</span>
      </div>
      <ul className="mt-3 divide-y divide-line">
        {rows.map(([id, title, status, pct]) => (
          <li key={id} className="flex items-center gap-3 py-2.5">
            <span className="w-12 shrink-0 text-[11px] tabular-nums text-muted">{id}</span>
            <span className="min-w-0 flex-1 truncate text-[14px] font-medium">{title}</span>
            <span className={cn("hidden shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-medium sm:inline", tone[status])}>{status}</span>
            <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-panel">
              <span data-story-bar="" className="block h-full origin-left rounded-full bg-ink" style={{ width: `${pct}%` }} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PeopleMock() {
  const people: [string, string, string, string][] = [
    ["Aarav Sharma", "Engineering", "On shift", "AS"],
    ["Riya Kapoor", "Design", "On leave", "RK"],
    ["Jon Smith", "Sales", "On shift", "JS"],
    ["Priya Menon", "Finance", "Remote", "PM"],
  ];
  const tone: Record<string, string> = {
    "On shift": "bg-accent-soft text-accent",
    "On leave": "bg-[#fdf3c9] text-[#a67c00]",
    Remote: "bg-[#e3ecfc] text-[#3d6fd8]",
  };
  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_1.4fr]">
      <div className={cn(white, "flex flex-col justify-between p-5")}>
        <p className="text-[12px] text-muted">Headcount</p>
        <p className="mt-1 text-[34px] font-medium leading-none tracking-[-0.03em]">146</p>
        <div className="mt-5 grid grid-cols-2 gap-2 text-[11.5px]">
          <div className="rounded-[12px] bg-panel p-2.5">
            <p className="text-muted">Open roles</p>
            <p className="mt-0.5 font-medium">9</p>
          </div>
          <div className="rounded-[12px] bg-panel p-2.5">
            <p className="text-muted">Attendance</p>
            <p className="mt-0.5 font-medium">96%</p>
          </div>
        </div>
      </div>
      <ul className={cn(white, "divide-y divide-line p-2")}>
        {people.map(([name, team, status, initials]) => (
          <li key={name} className="flex items-center gap-3 p-2.5">
            <span className="grid size-8 place-items-center rounded-full bg-ink text-[10px] font-semibold text-white">{initials}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-medium">{name}</span>
              <span className="block text-[11px] text-muted">{team}</span>
            </span>
            <span className={cn("rounded-full px-2 py-0.5 text-[10.5px] font-medium", tone[status])}>{status}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FinanceMock() {
  const invoices: [string, string, string, string][] = [
    ["INV-1042", "Acme Retail", "$1,240", "Paid"],
    ["INV-1043", "Globex Ltd", "$865", "Overdue"],
    ["INV-1044", "Northwind", "$2,100", "Sent"],
  ];
  const tone: Record<string, string> = {
    Paid: "bg-accent-soft text-accent",
    Overdue: "bg-[#fde2e1] text-[#c2413b]",
    Sent: "bg-panel text-muted",
  };
  const ageing = [
    ["0–30 days", 64],
    ["31–60 days", 24],
    ["60+ days", 12],
  ] as const;
  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_1.35fr]">
      <div className={cn(white, "p-5")}>
        <p className="text-[12px] text-muted">Receivables</p>
        <p className="mt-1 text-[30px] font-medium leading-none tracking-[-0.03em]">$18,200</p>
        <div className="mt-5 space-y-3">
          {ageing.map(([label, pct]) => (
            <div key={label}>
              <div className="flex justify-between text-[11.5px]">
                <span>{label}</span>
                <span className="text-muted">{pct}%</span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-panel">
                <div data-story-bar="" className={cn("h-full origin-left rounded-full", pct > 20 ? "bg-[#5fd9cc]" : "bg-[#f0a54a]")} style={{ width: `${pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={cn(white, "p-4")}>
        <p className="text-[14px] font-medium">Recent invoices</p>
        <ul className="mt-2 divide-y divide-line">
          {invoices.map(([id, client, amount, status]) => (
            <li key={id} className="flex items-center gap-3 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-medium">{client}</span>
                <span className="block text-[11px] tabular-nums text-muted">{id}</span>
              </span>
              <span className="text-[14px] tabular-nums">{amount}</span>
              <span className={cn("w-16 rounded-full py-0.5 text-center text-[10.5px] font-medium", tone[status])}>{status}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted">
          <Clock className="size-3.5" /> GST-ready · double-entry · period locks
        </p>
      </div>
    </div>
  );
}

export function InsightsMock() {
  return <AnalyticsCard />;
}
